import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useMemo, useState } from 'react';
import { Box, Typography, TextField, Button, IconButton, Tooltip, } from '@mui/material';
import { Pencil } from 'lucide-react';
import VarianceBadge from '../../../common_components/VarianceBadge';
import { StyledCurrency } from '../../../helper/StyledCurrency';
/**
 * Renders a generated weekly-call document (Markdown) with the Build Prompt's
 * formatting rules applied:
 *
 *   • ▲ change cells green, ▼ red — EXCEPT in a CPC or Unsubscribed row,
 *     where the colour is inverted (a fall is favourable). Those two are the
 *     only exceptions (v9.5 §2.3/§4). Arrows are never rewritten.
 *   • [ bracketed fill-ins ] highlighted yellow.
 *   • A metric cell's trailing *italic run* is its inline definition line —
 *     drawn as a small gray sub-line under the name rather than inline, so the
 *     key costs no extra table row (§3.4).
 *   • Bullets nest four levels on two-space indentation, with the template's
 *     glyphs • ◦ – · (§2.2 / §5.4).
 *   • **bold**, headings, pipe tables, bullet/numbered lists, --- rules.
 *
 * Hand-rolled rather than pulling in a Markdown library, matching AiRichText's
 * reasoning: this builds React nodes, so model output can't inject markup.
 */
// ─── design tokens (from the Liverpool 7.27 reference agenda) ────────────────
const PINK = '#FF008E';
const FILL_BG = '#FEF7C3';
/** Body copy and metric labels. */
const INK = '#101828';
/** Prior-period actuals — deliberately quieter than the MTD column. */
const MUTED = '#667085';
/** Table head is a solid near-black band with white type. */
const HEAD_BG = '#101828';
const HEAD_FG = '#FFFFFF';
const ROW_BORDER = '#EAECF0';
/**
 * A numbered section heading — "01 · PAID SOCIAL" or "3. PAID SOCIAL". These
 * get the full-width pink band; an unnumbered heading at the same level (the
 * document title) does not, so the two can share `##` in the Markdown.
 */
const SECTION_HEADING_RE = /^\s*\d{1,2}\s*[.·)]\s+\S/;
/** A footnote line under a table — "† Net Revenue reflects returns…". */
const FOOTNOTE_RE = /^\s*[†‡§¶]/;
/** Counts and ratios: "3,354", "5.34x". Money and percents have their own test. */
const RATIO_OR_COUNT_RE = /^[\d,]+(?:\.\d+)?x?$/;
const BULLET_RE = /^\s*[-*•]\s+/;
const ORDERED_RE = /^\s*(\d+)[.)]\s+/;
const HEADING_RE = /^(#{1,6})\s+(.*)$/;
const TABLE_DIVIDER_RE = /^\s*\|?[\s:|-]+\|[\s:|-]*$/;
const RULE_RE = /^\s*(?:---+|===+|\*\*\*+)\s*$/;
const QUOTE_RE = /^\s*>\s?/;
/**
 * The spec's two callouts are written as blockquotes: a green-bordered
 * "Growth Angle" box per channel, and the red-flagged internal notes. Anything
 * else quoted gets the neutral treatment.
 */
const GROWTH_ANGLE_RE = /^\s*\**\s*growth angle/i;
const INTERNAL_FLAG_RE = /internal|do not share|flag/i;
/** A cell is a "change" cell when it leads with a direction arrow. */
const UP_RE = /^\s*▲/;
const DOWN_RE = /^\s*▼/;
/**
 * The two inverted metrics, and only these two: a fall in CPC or in
 * Unsubscribed is the favourable direction. CPOC, Total Spend, Discount Load
 * and Returns all stay pure-directional.
 */
const CPC_ROW_RE = /(^|[^A-Z])CPC([^A-Z]|$)/;
const UNSUBSCRIBED_ROW_RE = /unsubscrib/i;
const isInvertedMetric = (rowLabel) => CPC_ROW_RE.test(rowLabel) || UNSUBSCRIBED_ROW_RE.test(rowLabel);
/** Two spaces of indentation per bullet level, capped at the spec's four. */
const BULLET_LEVEL_RE = /^(\s*)[-*•]\s+/;
const MAX_BULLET_LEVEL = 3;
/** The reference agenda's glyph per level: ● then ○ then ■ then ▪. */
const BULLET_GLYPHS = ['●', '○', '■', '▪'];
/** The deeper glyphs are solid shapes, so they need to sit smaller to match. */
const BULLET_SIZES = ['7px', '7px', '6px', '6px'];
/**
 * A metric cell's inline definition (§3.4): the metric name, then the formula
 * as a trailing italic run — "**Net Sales** *gross_sales − discounts −
 * returns*". Returns null when the cell has no trailing italic, or when the
 * italic IS the whole cell (an ordinary italicised row label).
 *
 * The italic may be followed by a footnote marker — §3.4 requires one on Net
 * Sales and AOV, the two metrics whose caveats are too long to inline. Those
 * markers stay with the NAME, above the sub-line, which is where a reader
 * looks for them.
 */
// The marker class must NOT include '*', or the closing '**' of a bold-only
// cell ("**Spend**") is taken for a footnote marker and the metric name itself
// renders as the definition sub-line.
const TRAILING_ITALIC_RE = /^(.*?)\s*\*([^*]+)\*\s*([†‡§¶\d\s]*)$/;
function splitDefinition(cell) {
    const m = TRAILING_ITALIC_RE.exec(cell);
    if (!m)
        return null;
    const definition = m[2].trim();
    const marker = (m[3] || '').trim();
    // Re-attach the marker to the name so "Net Revenue †" reads as one label.
    const name = [m[1].trim(), marker].filter(Boolean).join(' ');
    if (!name || !definition)
        return null;
    return { name, definition };
}
// ─── inline ──────────────────────────────────────────────────────────────────
/**
 * A markdown link. Matched BEFORE the bare-bracket alternative below, or the
 * link text would be taken for a fill-in marker and the URL would spill into
 * the paragraph as literal text. §2.2 lets the client agenda link to assets the
 * client can actually open, so these are real content, not an edge case.
 */
const LINK_RE = /^\[([^\]]*)\]\((\S+)\)$/;
/** Only http(s) is followed — never javascript:, data: or a bare scheme. */
const SAFE_URL_RE = /^https?:\/\//i;
/**
 * Splits on [text](url) links, **bold**, *italic* and [ fill-in ] markers,
 * returning styled nodes. Bold is matched before the italic alternative so it
 * isn't eaten by it — the model uses italics for qualifiers inside metric names
 * and for the inline definition lines.
 */
function inline(text, key) {
    const parts = text
        .split(/(\[[^\]]*\]\(\S+\)|\*\*.+?\*\*|\*[^*\n]+\*|\[[^\]]*\])/g)
        .filter(Boolean);
    return parts.map((part, i) => {
        const link = LINK_RE.exec(part);
        if (link) {
            const [, label, href] = link;
            // An unsafe or relative href keeps its text but loses the anchor —
            // the reader still sees the name, just not as something clickable.
            if (!SAFE_URL_RE.test(href)) {
                return _jsx(React.Fragment, { children: label }, `${key}-${i}`);
            }
            return (_jsx(Box, { component: "a", href: href, target: "_blank", rel: "noopener noreferrer", sx: {
                    color: PINK,
                    textDecoration: 'underline',
                    textUnderlineOffset: '2px',
                    '&:hover': { textDecoration: 'none' },
                }, children: label }, `${key}-${i}`));
        }
        if (part.startsWith('*') &&
            part.endsWith('*') &&
            !part.startsWith('**') &&
            part.length > 2) {
            return (_jsx(Box, { component: "span", sx: { fontStyle: 'italic' }, children: part.slice(1, -1) }, `${key}-${i}`));
        }
        if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
            return (_jsx(Box, { component: "span", sx: { fontWeight: 600, color: '#101828' }, children: inline(part.slice(2, -2), `${key}-${i}b`) }, `${key}-${i}`));
        }
        // A bracketed placeholder — the spec's yellow fill-in marker. Ordinary
        // bracketed prose like [ongoing] gets the same treatment, which is fine:
        // both are things a human needs to look at.
        if (part.startsWith('[') && part.endsWith(']')) {
            return (_jsx(Box, { component: "span", sx: {
                    bgcolor: FILL_BG,
                    color: '#7A2E0E',
                    fontWeight: 600,
                    borderRadius: '4px',
                    px: 0.5,
                }, children: part }, `${key}-${i}`));
        }
        return _jsx(React.Fragment, { children: part }, `${key}-${i}`);
    });
}
/**
 * Every block is editable — headings, tables (performance figures included)
 * and narrative alike.
 *
 * Two consequences the editor warns about rather than prevents:
 *   • A hand-edited figure no longer matches `report.metrics`, the copy of the
 *     numbers the document was generated from. The model's original output is
 *     kept in `report.original` on first edit, so nothing is unrecoverable.
 *   • The renderer's conventions are plain text, so they can be typed over: a
 *     change cell needs its "▲ 12.0%" to draw a coloured chip, a metric cell
 *     needs "**Name** *formula*" for the gray sub-line, and a section heading
 *     needs its leading number for the pink band.
 *
 * A horizontal rule is the one exception: there is nothing in it to edit.
 */
const isEditableBlock = (block) => block.kind !== 'rule';
const splitRow = (line) => line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((c) => c.trim());
function parse(markdown) {
    const lines = (markdown || '').split(/\r?\n/);
    const blocks = [];
    let i = 0;
    const isTableStart = (idx) => lines[idx]?.includes('|') &&
        lines[idx + 1] !== undefined &&
        TABLE_DIVIDER_RE.test(lines[idx + 1]);
    while (i < lines.length) {
        const line = lines[i];
        if (!line.trim()) {
            i++;
            continue;
        }
        if (RULE_RE.test(line)) {
            blocks.push({ kind: 'rule', from: i, to: i + 1 });
            i++;
            continue;
        }
        const heading = HEADING_RE.exec(line);
        if (heading) {
            blocks.push({
                kind: 'heading', level: heading[1].length, text: heading[2],
                from: i, to: i + 1,
            });
            i++;
            continue;
        }
        if (QUOTE_RE.test(line)) {
            const start = i;
            const quoted = [];
            while (i < lines.length && QUOTE_RE.test(lines[i])) {
                quoted.push(lines[i].replace(QUOTE_RE, '').trim());
                i++;
            }
            blocks.push({ kind: 'quote', lines: quoted, from: start, to: i });
            continue;
        }
        if (isTableStart(i)) {
            const start = i;
            const header = splitRow(lines[i]);
            i += 2; // header + divider
            const rows = [];
            while (i < lines.length && lines[i].includes('|') && lines[i].trim()) {
                rows.push(splitRow(lines[i]));
                i++;
            }
            blocks.push({ kind: 'table', header, rows, from: start, to: i });
            continue;
        }
        if (BULLET_RE.test(line)) {
            const start = i;
            const items = [];
            while (i < lines.length && BULLET_RE.test(lines[i])) {
                const indent = BULLET_LEVEL_RE.exec(lines[i])?.[1] ?? '';
                // Tabs count as one level; two spaces per level otherwise.
                const width = indent.replace(/\t/g, '  ').length;
                items.push({
                    level: Math.min(MAX_BULLET_LEVEL, Math.floor(width / 2)),
                    text: lines[i].replace(BULLET_RE, '').trim(),
                });
                i++;
            }
            blocks.push({ kind: 'ul', items, from: start, to: i });
            continue;
        }
        if (ORDERED_RE.test(line)) {
            const start = i;
            const items = [];
            while (i < lines.length && ORDERED_RE.test(lines[i])) {
                items.push(lines[i].replace(ORDERED_RE, '').trim());
                i++;
            }
            blocks.push({ kind: 'ol', items, from: start, to: i });
            continue;
        }
        const paraStart = i;
        const paraLines = [];
        while (i < lines.length &&
            lines[i].trim() &&
            !HEADING_RE.test(lines[i]) &&
            !BULLET_RE.test(lines[i]) &&
            !ORDERED_RE.test(lines[i]) &&
            !RULE_RE.test(lines[i]) &&
            !QUOTE_RE.test(lines[i]) &&
            !isTableStart(i)) {
            paraLines.push(lines[i].trim());
            i++;
        }
        if (paraLines.length)
            blocks.push({ kind: 'para', lines: paraLines, from: paraStart, to: i });
        else
            i++; // never stall
    }
    return blocks;
}
// ─── change cells ────────────────────────────────────────────────────────────
/**
 * A "▲ 47.8%" / "▼ 12.3%" cell rendered as the portal's standard change pill.
 * Returns null when the cell isn't a change value, so the caller falls back to
 * ordinary text.
 */
function renderChangeCell(cell, rowLabel) {
    const up = UP_RE.test(cell);
    const down = DOWN_RE.test(cell);
    if (!up && !down)
        return null;
    // Strip the arrow — the badge draws its own icon.
    const label = cell.replace(/^\s*[▲▼]\s*/, '').trim();
    if (!label)
        return null;
    // Up is green, down is red — except in a CPC or Unsubscribed row, where a
    // fall is the favourable outcome. The arrow still points the true way.
    const favourable = isInvertedMetric(rowLabel) ? down : up;
    return (_jsx(VarianceBadge, { value: up ? 1 : -1, label: label, favourable: favourable, fontSize: "13px", fontWeight: 400 }));
}
/**
 * A cell holding nothing but a money or percent figure — "$2,673.75", "50.5%",
 * optionally wrapped in **bold** for the MTD column.
 *
 * These render through StyledCurrency, the portal's canonical $/% treatment:
 * the symbol goes in its own grey span with 2px of padding on the inner side,
 * so figures line up the same way they do on Account Summary, Divergence and
 * the other report modules. Ratios ("2.87x") and plain counts are left alone —
 * they carry no symbol.
 */
const MONEY_OR_PCT_RE = /^(?:\$[\d,]+(?:\.\d+)?|[\d,]+(?:\.\d+)?%)$/;
/**
 * Column emphasis, following the reference agenda: the current-period column
 * carries the weight, and the prior-period actuals beside it are deliberately
 * quieter so the eye lands on "now" first and reads the comparison second.
 * Column 1 is the current period; 2 and beyond are comparisons.
 */
const isCurrentPeriodColumn = (ci) => ci === 1;
function renderValueCell(cell, ci) {
    const bold = cell.startsWith('**') && cell.endsWith('**') && cell.length > 4;
    const plain = (bold ? cell.slice(2, -2) : cell).trim();
    if (!MONEY_OR_PCT_RE.test(plain))
        return null;
    const current = isCurrentPeriodColumn(ci);
    return (_jsx(StyledCurrency, { value: plain, valueWeight: current ? 600 : 400, valueColor: current ? INK : MUTED }));
}
/** Counts and ratios carry no symbol, so they bypass StyledCurrency. */
function renderPlainNumberCell(cell, ci) {
    const plain = cell.replace(/\*\*/g, '').trim();
    if (!RATIO_OR_COUNT_RE.test(plain))
        return null;
    const current = isCurrentPeriodColumn(ci);
    return (_jsx(Box, { component: "span", sx: {
            fontWeight: current ? 600 : 400,
            color: current ? INK : MUTED,
            fontStyle: current ? 'normal' : 'italic',
        }, children: plain }));
}
// ─── component ───────────────────────────────────────────────────────────────
/**
 * Heading scale, following the reference agenda:
 *   1  the brand eyebrow — "GROUP 8A · Weekly Performance", small pink caps
 *   2  the document title — "Liverpool Los Angeles — Weekly Call Agenda"
 *      (a NUMBERED level 2 is a section band instead; see below)
 *   3  a block heading inside a section — "Business-level metrics", pink
 *   4  a sub-label inside a block — "Brand" / "Non-Brand", dark
 */
const HEADING_SX = {
    1: {
        fontSize: 11.5,
        fontWeight: 700,
        color: PINK,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
    },
    2: { fontSize: 20, fontWeight: 700, color: INK, letterSpacing: '-0.01em' },
    3: { fontSize: 13, fontWeight: 700, color: PINK },
    4: { fontSize: 12.5, fontWeight: 700, color: INK },
};
const HEADING_MT = {
    1: 0.5,
    2: 0.25,
    3: 1.25,
    4: 0.75,
};
const AgendaDocument = ({ markdown, editable = false, onChange }) => {
    const blocks = useMemo(() => parse(markdown), [markdown]);
    /** Source span of the block being edited, keyed by its start line. */
    const [editingFrom, setEditingFrom] = useState(null);
    const [draft, setDraft] = useState('');
    const sourceLines = useMemo(() => (markdown || '').split(/\r?\n/), [markdown]);
    const beginEdit = (block) => {
        setEditingFrom(block.from);
        setDraft(sourceLines.slice(block.from, block.to).join('\n'));
    };
    const cancelEdit = () => {
        setEditingFrom(null);
        setDraft('');
    };
    /**
     * Splice the edited lines back into the document. Working on the source
     * lines rather than re-serialising the parsed blocks means every
     * convention the renderer depends on — table pipes, the ▲/▼ cells, the
     * definition italics — survives an edit to a neighbouring paragraph.
     */
    const commitEdit = (block) => {
        const replacement = draft.replace(/\r\n/g, '\n').split('\n');
        const next = [
            ...sourceLines.slice(0, block.from),
            ...replacement,
            ...sourceLines.slice(block.to),
        ].join('\n');
        onChange?.(next);
        cancelEdit();
    };
    return (_jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1 }, children: blocks.map((block, bi) => {
            // An editable narrative block, currently open in the editor.
            if (editable && editingFrom === block.from && isEditableBlock(block)) {
                return (_jsxs(Box, { sx: {
                        border: `1px solid ${PINK}`,
                        borderRadius: '8px',
                        p: 1.25,
                        bgcolor: '#FFFBFD',
                    }, children: [_jsx(TextField, { value: draft, onChange: (e) => setDraft(e.target.value), multiline: true, fullWidth: true, minRows: 3, autoFocus: true, slotProps: {
                                input: {
                                    sx: {
                                        fontFamily: "'Roboto Mono', ui-monospace, monospace",
                                        fontSize: '12.5px',
                                        lineHeight: 1.6,
                                        color: INK,
                                    },
                                },
                            } }), _jsxs(Box, { sx: { display: 'flex', gap: 1, mt: 1, alignItems: 'center' }, children: [_jsx(Button, { size: "small", variant: "contained", onClick: () => commitEdit(block), sx: {
                                        bgcolor: PINK, textTransform: 'none', fontWeight: 600,
                                        borderRadius: '8px', '&:hover': { bgcolor: '#E6007A' },
                                    }, children: "Apply" }), _jsx(Button, { size: "small", onClick: cancelEdit, sx: { textTransform: 'none', color: '#475467' }, children: "Cancel" }), _jsx(Typography, { sx: { fontSize: 11, color: MUTED, ml: 'auto', maxWidth: 420, textAlign: 'right' }, children: block.kind === 'table'
                                        ? 'One row per line. Keep the first two lines (headings, dashes) and the | separators. “▲ 12.0%” draws the green chip, “▼” the red one; “**Name** *formula*” sets the gray sub-line.'
                                        : block.kind === 'heading'
                                            ? 'A section heading keeps its leading number — “## 01 · PAID SOCIAL” — or it loses the pink band.'
                                            : 'Markdown — bullets use “- ”, two spaces per nesting level' })] })] }, bi));
            }
            const canEdit = editable && isEditableBlock(block);
            const rendered = (() => {
                switch (block.kind) {
                    case 'rule':
                        return (_jsx(Box, { sx: { borderTop: '1px solid #EAECF0', my: 0.5 } }, bi));
                    case 'heading': {
                        // A numbered heading is a section divider: a solid pink
                        // band spanning the page with white caps, exactly as the
                        // reference agenda sets "1. HIGH-LEVEL PERFORMANCE".
                        if (block.level <= 2 && SECTION_HEADING_RE.test(block.text)) {
                            return (_jsx(Box, { sx: {
                                    mt: 2.5,
                                    mb: 0.5,
                                    bgcolor: PINK,
                                    borderRadius: '3px',
                                    px: 1.5,
                                    py: 0.9,
                                }, children: _jsx(Typography, { className: "all-Font-Sizes", sx: {
                                        fontFamily: "'Inter', sans-serif",
                                        fontSize: '13px !important',
                                        fontWeight: 700,
                                        color: HEAD_FG,
                                        letterSpacing: '0.04em',
                                        textTransform: 'uppercase',
                                        lineHeight: 1.35,
                                    }, children: inline(block.text, `h${bi}`) }) }, bi));
                        }
                        const sx = HEADING_SX[block.level] || HEADING_SX[4];
                        return (_jsx(Box, { sx: { mt: HEADING_MT[block.level] ?? HEADING_MT[4] }, children: _jsx(Typography, { sx: sx, children: inline(block.text, `h${bi}`) }) }, bi));
                    }
                    // Four nesting levels with the template's own glyphs, so
                    // the list markers can't be re-picked by the browser as
                    // the depth changes. Indentation is drawn rather than
                    // built from nested <ul>s: the source is a flat run of
                    // lines with two-space steps, and a flat render keeps a
                    // skipped level (1 → 3) from collapsing.
                    // The spec's callouts: green-bordered for a Growth Angle,
                    // red for an internal-only flag, neutral for anything else.
                    case 'quote': {
                        const first = block.lines[0] || '';
                        const accent = GROWTH_ANGLE_RE.test(first)
                            ? { border: '#17B26A', bg: '#F6FEF9' }
                            : INTERNAL_FLAG_RE.test(first)
                                ? { border: '#F04438', bg: '#FFFBFA' }
                                : { border: '#D0D5DD', bg: '#F9FAFB' };
                        return (_jsx(Box, { sx: {
                                borderLeft: `3px solid ${accent.border}`,
                                bgcolor: accent.bg,
                                borderRadius: '0 8px 8px 0',
                                px: 1.25,
                                py: 1,
                            }, children: block.lines.map((line, li) => (_jsx(Typography, { className: "all-Font-Sizes", sx: {
                                    fontFamily: "'Inter', sans-serif",
                                    fontSize: '13px !important',
                                    color: '#475467',
                                    lineHeight: 1.6,
                                }, children: inline(line, `q${bi}-${li}`) }, li))) }, bi));
                    }
                    case 'ul':
                        return (_jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 0.4 }, children: block.items.map((item, ii) => (_jsxs(Box, { sx: {
                                    display: 'flex',
                                    gap: 0.75,
                                    pl: 1 + item.level * 1.75,
                                }, children: [_jsx(Box, { component: "span", "aria-hidden": true, sx: {
                                            flex: '0 0 auto',
                                            // Dark at the top level, quieter as it
                                            // nests — the reference agenda keeps its
                                            // glyphs neutral and lets the text lead.
                                            color: item.level === 0 ? INK : '#98A2B3',
                                            fontSize: BULLET_SIZES[item.level],
                                            // Nudged onto the text baseline; the
                                            // glyphs are small and centre too high.
                                            lineHeight: '22px',
                                        }, children: BULLET_GLYPHS[item.level] }), _jsx(Typography, { className: "all-Font-Sizes", sx: {
                                            fontFamily: "'Inter', sans-serif",
                                            fontSize: '13px !important',
                                            color: '#475467',
                                            lineHeight: 1.55,
                                        }, children: inline(item.text, `l${bi}-${ii}`) })] }, ii))) }, bi));
                    case 'ol':
                        return (_jsx(Box, { component: "ol", sx: { m: 0, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 0.4 }, children: block.items.map((item, ii) => (_jsx(Typography, { component: "li", className: "all-Font-Sizes", sx: {
                                    fontFamily: "'Inter', sans-serif",
                                    fontSize: '13px !important',
                                    color: '#475467',
                                    lineHeight: 1.55,
                                    // Takeaways are numbered, and the reference
                                    // agenda sets those numerals in brand pink.
                                    '&::marker': { color: PINK, fontWeight: 700 },
                                }, children: inline(item, `l${bi}-${ii}`) }, ii))) }, bi));
                    case 'table':
                        return (_jsx(Box, { sx: {
                                overflowX: 'auto',
                                border: `1px solid ${ROW_BORDER}`,
                                borderRadius: '6px',
                                // Clips the dark head band to the rounded corners.
                                overflow: 'hidden',
                                mt: 0.5,
                            }, children: _jsxs(Box, { component: "table", sx: { borderCollapse: 'collapse', width: '100%', minWidth: 480 }, children: [_jsx(Box, { component: "thead", children: _jsx(Box, { component: "tr", children: block.header.map((h, hi) => (_jsx(Box, { component: "th", className: "all-Font-Sizes", sx: {
                                                    textAlign: 'left',
                                                    fontFamily: "'Inter', sans-serif",
                                                    fontSize: '12px !important',
                                                    fontWeight: 700,
                                                    color: HEAD_FG,
                                                    bgcolor: HEAD_BG,
                                                    px: 1.25,
                                                    py: 0.9,
                                                    whiteSpace: 'nowrap',
                                                }, children: inline(h, `th${bi}-${hi}`) }, hi))) }) }), _jsx(Box, { component: "tbody", children: block.rows.map((row, ri) => (_jsx(Box, { component: "tr", children: row.map((cell, ci) => {
                                                // §3.4: the metric column carries its own
                                                // definition as a trailing italic run,
                                                // drawn as a sub-line beneath the name so
                                                // the key costs no extra table row.
                                                const definition = ci === 0 ? splitDefinition(cell) : null;
                                                const rendered = ci === 0
                                                    ? null
                                                    : renderChangeCell(cell, row[0] || '') ??
                                                        renderValueCell(cell, ci) ??
                                                        renderPlainNumberCell(cell, ci);
                                                return (_jsx(Box, { component: "td", className: "all-Font-Sizes", sx: {
                                                        fontFamily: "'Inter', sans-serif",
                                                        fontSize: '13px !important',
                                                        fontWeight: 400,
                                                        color: '#475467',
                                                        borderBottom: `1px solid ${ROW_BORDER}`,
                                                        px: 1.25,
                                                        py: 0.7,
                                                        verticalAlign: 'top',
                                                        // First column is the row label. It must wrap:
                                                        // follow-up tables put whole sentences here,
                                                        // and nowrap pushed Owner/Status out of view.
                                                        // Metric column runs wider than the rest —
                                                        // §3.4 takes that width from the MoM/YoY
                                                        // actual columns, which are wider than
                                                        // their figures need, to pay for the
                                                        // inline definition sub-line.
                                                        ...(ci === 0
                                                            ? { color: INK, fontWeight: 600, minWidth: 160 }
                                                            : {}),
                                                    }, children: definition ? (_jsxs(_Fragment, { children: [inline(definition.name.replace(/\*\*/g, ''), `td${bi}-${ri}-${ci}`), _jsx(Box, { sx: {
                                                                    fontSize: '10px',
                                                                    fontStyle: 'italic',
                                                                    color: '#595959',
                                                                    lineHeight: 1.4,
                                                                    mt: '1px',
                                                                }, children: definition.definition })] })) : (rendered ??
                                                        // Every body cell stays 400 weight, even when the
                                                        // model bolds a figure like "68"/"2.47x" — strip the
                                                        // markers so inline()'s bold span never overrides it.
                                                        inline(cell.replace(/\*\*/g, ''), `td${bi}-${ri}-${ci}`)) }, ci));
                                            }) }, ri))) })] }) }, bi));
                    case 'para':
                    default:
                        return (_jsx(Box, { children: block.lines.map((line, li) => {
                                // A footnote under a table — "† Net Revenue
                                // reflects returns…" — is set small and
                                // italic so it reads as apparatus, not copy.
                                const footnote = FOOTNOTE_RE.test(line);
                                return (_jsx(Typography, { className: "all-Font-Sizes", sx: {
                                        fontFamily: "'Inter', sans-serif",
                                        fontSize: footnote
                                            ? '11px !important'
                                            : '13px !important',
                                        color: footnote ? MUTED : '#475467',
                                        fontStyle: footnote ? 'italic' : 'normal',
                                        lineHeight: footnote ? 1.5 : 1.6,
                                    }, children: inline(line, `p${bi}-${li}`) }, li));
                            }) }, bi));
                }
            })();
            if (!canEdit)
                return rendered;
            // Hovering a narrative block reveals its edit affordance. The
            // pencil sits outside the content so it never shifts the text.
            return (_jsxs(Box, { sx: {
                    position: 'relative',
                    '&:hover .agenda-edit-btn': { opacity: 1 },
                    '&:hover': {
                        outline: '1px dashed #F6C8E2',
                        outlineOffset: '4px',
                        borderRadius: '4px',
                    },
                }, children: [rendered, _jsx(Tooltip, { title: "Edit this block", children: _jsx(IconButton, { className: "agenda-edit-btn", size: "small", onClick: () => beginEdit(block), sx: {
                                position: 'absolute',
                                top: -6,
                                right: -6,
                                opacity: 0,
                                transition: 'opacity 120ms',
                                bgcolor: '#fff',
                                border: `1px solid ${ROW_BORDER}`,
                                color: PINK,
                                '&:hover': { bgcolor: '#FFF1F8', borderColor: PINK },
                            }, children: _jsx(Pencil, { size: 13 }) }) })] }, bi));
        }) }));
};
export default AgendaDocument;
